import { ReactNode } from 'react';
import { pipe } from 'fp-ts/function';
import { Card, Container, Grid, GridItem, IconName, Stack } from '@optimus-web/core';
import {
    ContentContainer,
    LocalizedLabel,
    LocalizedPageTitle,
    ModalBodyWrapper,
    ModalWrapper,
    OverlayedLoader,
    withAsyncContainer,
} from '@mews-commander/ui';
import { TextKey, ActionType } from '@mews-commander/enums';
import {
    LocalizedCurrencyValue,
    LocalizedText,
    createZodFormResolver,
    showSuccessAlert,
    useLocalizeText,
    Form,
    lt,
} from '@mews/framework';
import { AlternativePaymentMethodIntegrations, useInvalidateRepositoryQuery } from '@mews-commander/data-access';
import {
    useDeleteAlternativePaymentMethodIntegration,
    useGetAlternativePaymentMethodIntegration,
    useUpdateAlternativePaymentMethodIntegration,
} from '../../queries';
import ModalHeader from '../../../../../legacy/React/Components/Common/ModalHeader';
import { useCloseButton } from '../../../../../core/windowManager/hooks';
import { HeadingAction } from '../../../../../legacy/React/Components/HeadingAction/Action/types';
import { AlternativePaymentMethodIntegrationFormContent } from './AlternativePaymentMethodIntegrationFormContent';
import { alternativePaymentMethodIntegrationFormSchema } from './schema';
import { getSuccessfulSubmitTextKey, getUpdatedPaymentMethods } from './utils';

const AlternativePaymentMethodIntegrationContent = pipe(
    ({ children }: { children: ReactNode }) => (
        <Container padding="200">
            <Stack vertical spacing="200">
                {children}
            </Stack>
        </Container>
    ),
    withAsyncContainer
);

export type PaymentMethodsState = Record<AlternativePaymentMethodIntegrations, boolean>;

export const AlternativePaymentMethodIntegration = ({ id }: { id: string }) => {
    const getIntegration = useGetAlternativePaymentMethodIntegration({ id });
    const updateIntegration = useUpdateAlternativePaymentMethodIntegration();
    const deleteIntegration = useDeleteAlternativePaymentMethodIntegration({ id });
    const invalidateRepositoryQuery = useInvalidateRepositoryQuery();

    const closeButton = useCloseButton();
    const localizeText = useLocalizeText();

    const actions: HeadingAction[] = [
        {
            icon: IconName.Delete,
            buttonText: <LocalizedText textKey={TextKey.Delete} />,
            type: ActionType.ConfirmationDialog,
            text: <LocalizedText textKey={TextKey.DeleteHint} />,
            title: (
                <LocalizedText
                    textKey={TextKey.DeleteConfirmation}
                    substitutes={{ entity: localizeText({ textKey: TextKey.MewsAlternativePaymentMethods }) }}
                />
            ),
            isDanger: true,
            onConfirmation: () => deleteIntegration.mutate(),
        },
    ];

    const { data } = getIntegration;
    const supportedPaymentMethods = Object.values(AlternativePaymentMethodIntegrations);
    const formPaymentMethodsInitialState = supportedPaymentMethods.reduce(
        (formMethods, method) => ({ [method]: !!data?.paymentMethods.includes(method), ...formMethods }),
        {} as PaymentMethodsState
    );

    return (
        <ModalWrapper>
            <ModalHeader
                title={<LocalizedText textKey={TextKey.MewsAlternativePaymentMethods} />}
                primaryAction={closeButton}
                actions={actions}
            />
            <ModalBodyWrapper>
                <ContentContainer condensed>
                    <AlternativePaymentMethodIntegrationContent
                        isFetching={getIntegration.isLoading}
                        hasFailed={getIntegration.isError}
                    >
                        {(getIntegration.isFetching || updateIntegration.isPending || deleteIntegration.isPending) && (
                            <OverlayedLoader show fixed />
                        )}
                        {data && (
                            <Grid templateColumns="1fr 2fr">
                                <GridItem>
                                    <LocalizedPageTitle textKey={TextKey.Integration} />
                                    <Card padding="200">
                                        <Form
                                            onSubmit={(formValues) =>
                                                updateIntegration.mutate(
                                                    {
                                                        id,
                                                        paymentMethods: supportedPaymentMethods.filter(
                                                            (paymentMethod) => formValues[paymentMethod] === true
                                                        ),
                                                    },
                                                    {
                                                        onSuccess: (_: unknown, { paymentMethods }) => {
                                                            const updatedPaymentMethods = getUpdatedPaymentMethods(
                                                                formPaymentMethodsInitialState,
                                                                paymentMethods
                                                            );
                                                            const description =
                                                                getSuccessfulSubmitTextKey(updatedPaymentMethods);
                                                            showSuccessAlert({ description: lt(description) });
                                                            void invalidateRepositoryQuery('paymentGateways', 'get');
                                                        },
                                                    }
                                                )
                                            }
                                            defaultValues={formPaymentMethodsInitialState}
                                            resolver={createZodFormResolver(
                                                alternativePaymentMethodIntegrationFormSchema
                                            )}
                                        >
                                            {({ submit }) => (
                                                <AlternativePaymentMethodIntegrationFormContent
                                                    onSubmit={submit}
                                                    meta={data}
                                                />
                                            )}
                                        </Form>
                                    </Card>
                                </GridItem>

                                {data.isPurchased && data.monthlyFee && (
                                    <GridItem>
                                        <LocalizedPageTitle textKey={TextKey.Pricing} />
                                        <Card padding="200">
                                            <Stack distribution="space-between">
                                                <LocalizedLabel textKey={TextKey.MonthlyFee} as="div" />
                                                <LocalizedCurrencyValue
                                                    value={{
                                                        currency: data.monthlyFee.currencyCode,
                                                        value: data.monthlyFee.value,
                                                    }}
                                                />
                                            </Stack>
                                        </Card>
                                    </GridItem>
                                )}
                            </Grid>
                        )}
                    </AlternativePaymentMethodIntegrationContent>
                </ContentContainer>
            </ModalBodyWrapper>
        </ModalWrapper>
    );
};
